import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DataGridView,
  Input,
  Button,
  Row,
  OutlinedButton,
} from "@neumetric/blueprint-lib/components";
import { mitigatorActions, getRatingListByType } from "../../../store";

const RATING_TYPE_OPTIONS = [
  { id: "confidentiality", label: "Confidentiality" },
  { id: "integrity", label: "Integrity" },
  { id: "availability", label: "Availability" },
  { id: "security", label: "Security" },
  { id: "privacy", label: "Privacy" },
  { id: "impact", label: "Impact" },
  { id: "acceptance", label: "Acceptance" },
];

export const MitigatorRatingTrackerScreen = () => {
  const dispatch = useDispatch();
  const [selectedType, setSelectedType] = React.useState("confidentiality");
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const selectedLabel =
    RATING_TYPE_OPTIONS.find((o) => o.id === selectedType)?.label ||
    "Description";
  const _dvgRef = React.useRef(null);
  const _dvgAccRef = React.useRef(null);

  React.useEffect(() => {
    dispatch(mitigatorActions.fetchRatings());
  }, [dispatch]);
  const rows = useSelector(getRatingListByType.bind(null, selectedType));
  const acceptanceRows = useSelector(
    getRatingListByType.bind(null, "acceptance")
  );

  const columns = React.useMemo(() => {
    return [
      {
        dataIndex: "rating",
        typeRepresentation: "DOT",
        title: "Rating",
        width: "15%",
        editableFlag: isEditMode,
      },
      {
        dataIndex: "score",
        typeRepresentation: "TEXT",
        title: "Score",
        width: "10%",
        editableFlag: isEditMode,
      },
      {
        dataIndex: "description",
        typeRepresentation: "TEXTAREA",
        title: selectedLabel,
        width: "75%",
        rows: 3,
        editableFlag: isEditMode,
      },
    ];
  }, [selectedLabel, isEditMode]);
  const acceptanceColumns = React.useMemo(() => {
    return [
      {
        dataIndex: "rating",
        typeRepresentation: "DOT",
        title: "Rating",
        width: "15%",
        editableFlag: isEditMode,
      },
      {
        dataIndex: "description",
        typeRepresentation: "TEXTAREA",
        title: "Description",
        width: "85%",
        rows: 3,
        editableFlag: isEditMode,
      },
    ];
  }, [isEditMode]);

  const getCellEditablePolicy = React.useCallback(() => {
    return isEditMode ? "ALWAYS" : "NEVER";
  }, [isEditMode]);

  const getCellSemantics = React.useCallback(
    (col) => {
      const semantics = { className: "" };

      if (col?.dataIndex === "score") {
        semantics.className = "nm-score-top";

        if (isEditMode) {
          semantics.possibleValues = [
            { id: 0, label: "0" },
            { id: 1, label: "1" },
            { id: 2, label: "2" },
            { id: 3, label: "3" },
            { id: 4, label: "4" },
            { id: 5, label: "5" },
          ];
        }
      }

      return semantics;
    },
    [isEditMode]
  );

  const getCellTypeRepresentation = React.useCallback(
    (col) => {
      if (col.dataIndex === "rating") return "DOT";
      if (isEditMode && col.dataIndex === "description") return "TEXTAREA";
      return col.typeRepresentation;
    },
    [isEditMode]
  );

  const handleCancelClick = React.useCallback(() => {
    setIsEditMode(false);
    _dvgRef.current?.clearUpdatedDataSource?.();
    _dvgAccRef.current?.clearUpdatedDataSource?.();
  }, []);

  const handleSaveClick = React.useCallback(() => {
    const updatedMain = _dvgRef.current?.getUpdatedDataSource?.() || [];
    const updatedAcc = _dvgAccRef.current?.getUpdatedDataSource?.() || [];

    const originalMainById = new Map((rows || []).map((r) => [r.id, r]));
    const originalAccById = new Map(
      (acceptanceRows || []).map((r) => [r.id, r])
    );

    const isRowDirty = (r, o) => {
      if (!o) return true;
      const rRating = r?.rating?._id || r?.rating?.label || r?.rating || "";
      const oRating = o?.rating?._id || o?.rating?.label || o?.rating || "";
      return (
        rRating !== oRating ||
        Number(r?.score ?? -1) !== Number(o?.score ?? -1) ||
        String(r?.description ?? "") !== String(o?.description ?? "")
      );
    };

    const dirtyMain = updatedMain.filter((r) =>
      isRowDirty(r, originalMainById.get(r.id))
    );
    const dirtyAcc = updatedAcc.filter((r) =>
      isRowDirty(r, originalAccById.get(r.id))
    );

    const dirtyRows = [...dirtyMain, ...dirtyAcc];
    if (!dirtyRows.length) {
      setIsEditMode(false);
      _dvgRef.current?.clearUpdatedDataSource?.();
      _dvgAccRef.current?.clearUpdatedDataSource?.();
      return;
    }

    setIsSaving(true);
    const action = mitigatorActions.updateRatings({ rows: dirtyRows });

    action.onComplete = () => {
      setIsSaving(false);
      setIsEditMode(false);
      _dvgRef.current?.clearUpdatedDataSource?.();
      _dvgAccRef.current?.clearUpdatedDataSource?.();
      dispatch(mitigatorActions.fetchRatings());
    };

    action.onFailure = () => setIsSaving(false);

    dispatch(action);
  }, [dispatch, rows, acceptanceRows]);

  const gridRows = React.useMemo(
    () =>
      (rows || []).map((r) => ({ ...r, rating: r.rating?.label ?? r.rating })),
    [rows]
  );
  const gridAcceptanceRows = React.useMemo(
    () =>
      (acceptanceRows || []).map((r) => ({
        ...r,
        rating: r.rating?.label ?? r.rating,
      })),
    [rows]
  );

  return (
    <div
      className="mitigator-rating-screen w-100 h-100 o-auto pos-relative"
      style={{}}
    >
      <Row className="pos-absolute" style={{ top: 1, right: 16 }}>
        {isEditMode && (
          <OutlinedButton
            label="Cancel"
            icon="CANCEL"
            classes={{ button: "mr-16" }}
            onClick={handleCancelClick}
          />
        )}
        {!isEditMode ? (
          <Button
            label="Edit"
            icon="EDIT"
            onClick={() => setIsEditMode(true)}
          />
        ) : (
          <Button
            label={isSaving ? "Saving..." : "Save"}
            icon="SAVE"
            disabled={isSaving}
            onClick={handleSaveClick}
          />
        )}
      </Row>
      <div className={`w-1174-px h-592-px mb-24`}>
        {/* 🔹 Outer box container */}
        <div className="w-100 h-100" style={{ border: "1px solid #E0E0E0" }}>
          {/* Header row */}
          <div
            className="d-flex align-center pl-7"
            style={{
              borderBottom: "1px solid #E0E0E0",
              backgroundColor: "rgba(250, 250, 250, 1)",
            }}
          >
            <span className="ff-exo2 fw-600 f16" style={{ color: "#333" }}>
              CIASP Rating &amp; Description
            </span>

            <div
              style={{
                marginLeft: 30,
                width: 180,
                transform: "translateY(12px)",
              }}
            >
              <Input
                typeRepresentation="DROPDOWN"
                placeholder="Select Rating Type"
                options={RATING_TYPE_OPTIONS}
                value={selectedType}
                onChange={(val) => setSelectedType(val)}
                searchableFlag={false}
                multiSelection={false}
                template="DEFAULT"
              />
            </div>
          </div>
          <div className=" h-100">
            <DataGridView
              ref={_dvgRef}
              columns={columns}
              dataSource={isEditMode ? gridRows : rows}
              getCellSemantics={getCellSemantics}
              EmptyComponent={
                <span className="ff-exo2 f16 c777">
                  There are no ratings right now!
                </span>
              }
              getCellEditablePolicy={getCellEditablePolicy}
              getCellTypeRepresentation={getCellTypeRepresentation}
            />
          </div>
        </div>
      </div>
      <div className={`w-1250-px h-510-px `}>
        {/* 🔹 Outer box container */}
        <div className="w-100 h-100" style={{ border: "1px solid #E0E0E0" }}>
          {/* Header row */}
          <div
            className="d-flex align-center pl-7"
            style={{
              borderBottom: "1px solid #E0E0E0",
              backgroundColor: "rgba(250, 250, 250, 1)",
            }}
          >
            <span className="ff-exo2 fw-600 f16" style={{ color: "#333" }}>
              Acceptance Rating &amp; Recommendation
            </span>
          </div>
          <div className="h-100">
            <DataGridView
              ref={_dvgAccRef}
              columns={acceptanceColumns}
              dataSource={isEditMode ? gridAcceptanceRows : acceptanceRows}
              getCellSemantics={getCellSemantics}
              EmptyComponent={
                <span className="ff-exo2 f16 c777">
                  There are no ratings right now!
                </span>
              }
              getCellEditablePolicy={getCellEditablePolicy}
              getCellTypeRepresentation={getCellTypeRepresentation}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
